import type { ZoneTreeNode } from "@gettactic/api";
import { useZonesTreeQuery } from "@gettactic/hooks";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  MapIcon,
  PencilLine,
  Plus,
  Settings2,
  Square,
  SquareDashedMousePointer
} from "lucide-react";
import React from "react";
import { ZoneTypeDefinition } from "../../../../types";
import { IconButton } from "../../../ui/IconButton";
import { PanelShell } from "../../../ui/PanelShell";
import {
  useMapEditorActions,
  useMapEditorProps,
  useMapEditorSelection,
  useMapEditorTool,
  useMapEditorUI
} from "../../contexts/MapEditorContext";
import {
  EMPTY_ZONE_CHECKS,
  type ZoneChecks,
  ZoneChecksWatcher
} from "../../hooks/useZoneChecks";
import { EditorPanelComponentProps } from "../EditorLayout/EditorLayoutContext";

interface ZoneRow {
  zone: ZoneTreeNode;
  depth: number;
}

const flatten = (nodes: ZoneTreeNode[], depth = 0): ZoneRow[] =>
  nodes.flatMap((zone) => [
    { zone, depth },
    ...flatten(zone.children ?? [], depth + 1)
  ]);

/**
 * Zones side panel: the organisation's zones with what this map gives each
 * one, a one-click way to draw a region already tagged with a zone, and the
 * checks that keep the numbers honest (desks in no space, overlaps,
 * untagged regions).
 */
export const ZonesPanel: React.FC<EditorPanelComponentProps> = () => {
  const { selectedIds } = useMapEditorSelection();
  const {
    setSelectedIds,
    setActiveTool,
    setZoneToolTag,
    setShowZoneChecks,
    setShowZones,
    locateShape
  } = useMapEditorActions();
  const { activeTool, zoneToolTag, hasRoomPicker } = useMapEditorTool();
  const { showZoneChecks, showZones } = useMapEditorUI();
  const { data: tree, isLoading } = useZonesTreeQuery();
  const { zoneEditor: ZoneEditor } = useMapEditorProps();
  const [isOpen, setIsOpen] = React.useState(true);
  // The host's zone form, opened to create (optionally under a parent) or
  // to edit one of the listed zones.
  const [editor, setEditor] = React.useState<{
    zone?: ZoneTreeNode;
    parentId?: string | null;
  } | null>(null);

  // The zone tag only means something while a zone-making tool is active:
  // drawing a region, or picking detected rooms where that exists.
  const isTaggingTool =
    activeTool === "zone" || (hasRoomPicker && activeTool === "auto-zone");
  React.useEffect(() => {
    if (!isTaggingTool && zoneToolTag) setZoneToolTag(null);
  }, [isTaggingTool, zoneToolTag, setZoneToolTag]);

  // Containment as the server will see it on save, shared with the canvas
  // overlay and delivered only once the shapes settle.
  const [checks, setChecks] = React.useState<ZoneChecks>(EMPTY_ZONE_CHECKS);
  const { regions, untagged, conflicting } = checks;
  const unplacedSpotIds = React.useMemo(
    () => checks.unplaced.map((spot) => spot.id),
    [checks.unplaced]
  );
  const conflictShapeIds = React.useMemo(
    () => conflicting.map((region) => region.id),
    [conflicting]
  );

  const rows = React.useMemo(() => flatten(tree ?? []), [tree]);

  const statsByZone = React.useMemo(() => {
    const stats = new Map<
      string,
      { regionIds: string[]; spots: Set<string> }
    >();
    for (const region of regions) {
      if (!region.zone_id) continue;
      const entry = stats.get(region.zone_id) ?? {
        regionIds: [],
        spots: new Set()
      };
      entry.regionIds.push(region.id);
      for (const spotId of region.containedSpotIds ?? [])
        entry.spots.add(spotId);
      stats.set(region.zone_id, entry);
    }
    return stats;
  }, [regions]);

  const regionNameById = React.useMemo(
    () => new Map(regions.map((region) => [region.id, region.name])),
    [regions]
  );

  const selectShapes = (ids: string[]) => {
    if (ids.length === 0) return;
    // Selecting regions while they are hidden would show handles around
    // nothing: bring them back first.
    if (!showZones) setShowZones(true);
    setSelectedIds(ids);
    locateShape(ids[0]);
  };

  const startDrawing = (zone: ZoneTreeNode) => {
    const tag: ZoneTypeDefinition = {
      id: zone.id,
      name: zone.name,
      color: zone.color,
      icon: zone.icon ?? undefined
    };
    if (zoneToolTag?.id === zone.id && isTaggingTool) {
      setZoneToolTag(null);
      setActiveTool("select");
      return;
    }
    setZoneToolTag(tag);
    setActiveTool("zone");
  };

  const selectedRegionZoneId = React.useMemo(() => {
    const selected = regions.find((region) => selectedIds.includes(region.id));
    return selected?.zone_id ?? null;
  }, [regions, selectedIds]);

  const issues = untagged.length + unplacedSpotIds.length + conflicting.length;

  return (
    <div className="flex max-h-[50%] flex-none flex-col border-neutral-200 border-t">
      <ZoneChecksWatcher onChange={setChecks} />
      <PanelShell
        title="Zones"
        icon={MapIcon}
        actions={
          <>
            {ZoneEditor ? (
              <IconButton
                size="sm"
                label="New zone"
                onClick={() => setEditor({})}
                data-zone-new
              >
                <Plus size={14} />
              </IconButton>
            ) : null}
            <IconButton
              size="sm"
              label={
                showZones ? "Hide zones on the map" : "Show zones on the map"
              }
              isActive={!showZones}
              onClick={() => setShowZones(!showZones)}
              data-zones-visibility
            >
              {showZones ? <Eye size={14} /> : <EyeOff size={14} />}
            </IconButton>
            <IconButton
              size="sm"
              label={isOpen ? "Collapse zones" : "Expand zones"}
              onClick={() => setIsOpen((value) => !value)}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </IconButton>
          </>
        }
        className={isOpen ? "min-h-0" : "h-10"}
      >
        {isOpen ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!showZones ? (
              <p className="m-2 rounded-md bg-neutral-100 px-2 py-1.5 text-[11px] text-neutral-600">
                Zones are hidden on the map while you build. They are still
                saved; use the eye above to show them again.
              </p>
            ) : null}
            {zoneToolTag && activeTool === "zone" ? (
              <div className="m-2 rounded-md bg-blue-50 px-2 py-1.5 text-[11px] text-blue-700">
                <p>
                  Drag on the map to draw a region for{" "}
                  <strong>{zoneToolTag.name}</strong>. Draw as many as you need;
                  press Esc or pick Select to stop.
                </p>
                {hasRoomPicker ? (
                  <button
                    type="button"
                    className="mt-1.5 flex items-center gap-1 font-medium text-blue-700 hover:underline"
                    onClick={() => setActiveTool("auto-zone")}
                  >
                    <SquareDashedMousePointer size={12} />
                    Pick rooms from the walls instead
                  </button>
                ) : null}
              </div>
            ) : null}
            {zoneToolTag && hasRoomPicker && activeTool === "auto-zone" ? (
              <div className="m-2 rounded-md bg-blue-50 px-2 py-1.5 text-[11px] text-blue-700">
                <p>
                  Click rooms on the map to make them{" "}
                  <strong>{zoneToolTag.name}</strong> zones; Shift-click merges
                  a room into the previous pick. Create them from the card on
                  the map.
                </p>
                <button
                  type="button"
                  className="mt-1.5 flex items-center gap-1 font-medium text-blue-700 hover:underline"
                  onClick={() => setActiveTool("zone")}
                >
                  <PencilLine size={12} />
                  Draw a region instead
                </button>
              </div>
            ) : null}

            {isLoading ? (
              <p className="px-3 py-2 text-[11px] text-neutral-400">
                Loading zones…
              </p>
            ) : rows.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-neutral-400">
                <p>
                  No zones yet.{" "}
                  {ZoneEditor
                    ? "Create the organisation's first zone, then draw its regions on the map."
                    : 'Pick a zone shape and use "+ New" in the inspector, or create zones in Settings.'}
                </p>
                {ZoneEditor ? (
                  <button
                    type="button"
                    className="mt-1.5 flex items-center gap-1 font-medium text-blue-600 hover:underline"
                    onClick={() => setEditor({})}
                    data-zone-new
                  >
                    <Plus size={12} />
                    New zone
                  </button>
                ) : null}
              </div>
            ) : (
              <ul className="py-1">
                {rows.map(({ zone, depth }) => {
                  const stats = statsByZone.get(zone.id);
                  const isDrawing =
                    zoneToolTag?.id === zone.id && isTaggingTool;
                  const isCurrent = selectedRegionZoneId === zone.id;
                  return (
                    <li
                      key={zone.id}
                      className={`group flex items-center gap-2 px-2 py-1 text-xs ${isCurrent ? "bg-blue-50" : "hover:bg-neutral-50"}`}
                      style={{ paddingLeft: `${8 + depth * 12}px` }}
                    >
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        onClick={() => selectShapes(stats?.regionIds ?? [])}
                        title={
                          stats
                            ? "Select this zone's regions on the map"
                            : "Not drawn on this map yet"
                        }
                      >
                        <span
                          className="inline-block h-3 w-3 shrink-0 rounded-sm border border-black/10"
                          style={{ background: zone.color }}
                        />
                        <span className="truncate text-neutral-800">
                          {zone.name}
                        </span>
                        <span className="ml-auto shrink-0 text-[11px] text-neutral-400">
                          {stats
                            ? `${stats.spots.size} desk${stats.spots.size === 1 ? "" : "s"} · ${stats.regionIds.length}`
                            : "—"}
                        </span>
                      </button>
                      {ZoneEditor ? (
                        <>
                          <IconButton
                            size="sm"
                            label={`Add a zone under ${zone.name}`}
                            onClick={() => setEditor({ parentId: zone.id })}
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <Plus size={13} />
                          </IconButton>
                          <IconButton
                            size="sm"
                            label={`Edit ${zone.name}`}
                            onClick={() => setEditor({ zone })}
                            className="opacity-0 group-hover:opacity-100"
                            data-zone-edit={zone.id}
                          >
                            <Settings2 size={13} />
                          </IconButton>
                        </>
                      ) : null}
                      <IconButton
                        size="sm"
                        label={
                          isDrawing
                            ? "Stop drawing"
                            : `Draw a region for ${zone.name}`
                        }
                        isActive={isDrawing}
                        onClick={() => startDrawing(zone)}
                        className={
                          isDrawing ? "" : "opacity-0 group-hover:opacity-100"
                        }
                      >
                        {isDrawing ? (
                          <Square size={13} />
                        ) : (
                          <PencilLine size={13} />
                        )}
                      </IconButton>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="border-neutral-200 border-t px-3 py-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="flex items-center gap-1 font-medium text-[11px] text-neutral-600">
                  {issues > 0 ? (
                    <AlertTriangle size={12} className="text-amber-600" />
                  ) : null}
                  Checks
                </p>
                <label className="flex items-center gap-1 text-[11px] text-neutral-500">
                  <input
                    type="checkbox"
                    checked={showZoneChecks}
                    onChange={(event) =>
                      setShowZoneChecks(event.target.checked)
                    }
                  />
                  Show on map
                </label>
              </div>
              {issues === 0 ? (
                <p className="text-[11px] text-neutral-400">
                  Every desk is in a space, nothing overlaps.
                </p>
              ) : (
                <ul className="flex flex-col gap-1 text-[11px] text-neutral-600">
                  {unplacedSpotIds.length > 0 ? (
                    <li className="flex items-center justify-between gap-2">
                      <span>
                        {unplacedSpotIds.length} desk
                        {unplacedSpotIds.length === 1 ? "" : "s"} in no space
                      </span>
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => selectShapes(unplacedSpotIds)}
                      >
                        Select
                      </button>
                    </li>
                  ) : null}
                  {untagged.length > 0 ? (
                    <li className="flex items-center justify-between gap-2">
                      <span>
                        {untagged.length} open area
                        {untagged.length === 1 ? "" : "s"} without a zone
                      </span>
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() =>
                          selectShapes(untagged.map((region) => region.id))
                        }
                      >
                        Select
                      </button>
                    </li>
                  ) : null}
                  {conflicting.length > 0 ? (
                    <li className="flex items-center justify-between gap-2">
                      <span>
                        Overlap without nesting:{" "}
                        {conflictShapeIds
                          .map((id) => regionNameById.get(id) ?? id)
                          .join(", ")}
                      </span>
                      <button
                        type="button"
                        className="shrink-0 text-blue-600 hover:underline"
                        onClick={() => selectShapes(conflictShapeIds)}
                      >
                        Select
                      </button>
                    </li>
                  ) : null}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </PanelShell>
      {ZoneEditor ? (
        <ZoneEditor
          isOpen={editor !== null}
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
          zone={editor?.zone}
          parentId={editor?.parentId}
        />
      ) : null}
    </div>
  );
};
